import { ClubBookingApiClient } from "../../common/api/ClubBookingApiClient";
import { Promotion } from "../../model/pointofsale/Promotion";
import { MemberPromotion } from "../../model/pointofsale/MemberPromotion";
import { PromotionPreview } from "../../model/pointofsale/PromotionPreview";
import { CreatePromotionRequest } from "../../requests/pointofsale/CreatePromotionRequest";
import { UpdatePromotionRequest } from "../../requests/pointofsale/UpdatePromotionRequest";
import { ApplyPromotionRequest } from "../../requests/pointofsale/ApplyPromotionRequest";

// Promotion definition CRUD (FORT-313 API). Tenant scoping is handled by the auto-injected
// X-Client-Id header (bare paths, like PointOfSaleService), so no clientId in the path.
export class PromotionService extends ClubBookingApiClient {
    // showError so a refusal is a visible notification and a rejected promise, not an empty
    // list: GET /promotions is authorization-gated (CanReadPromotions), and the base client
    // otherwise swallows the 403 into `undefined` — which read as "this club has no
    // promotions" in the apply modal (FORT-398). Callers must handle the rejection.
    async getPromotions(): Promise<Promotion[]> {
        const promotions = await this.get<Promotion[]>(
            '/promotions', undefined, { showLoading: false, showError: true });
        return promotions ?? [];
    }

    async createPromotion(request: CreatePromotionRequest): Promise<Promotion> {
        return this.post<Promotion>('/promotions', request, { successMessage: "Promotion created." });
    }

    async updatePromotion(id: number, request: UpdatePromotionRequest): Promise<Promotion> {
        return this.put<Promotion>(`/promotions/${id}`, request, { successMessage: "Promotion saved." });
    }

    async deletePromotion(id: number): Promise<void> {
        await this.delete<void>(`/promotions/${id}`, { successMessage: "Promotion deactivated." });
    }

    // Apply a promotion to a member (FORT-314) — charges the fee and updates the member. Returns the
    // created history row.
    async applyPromotion(membershipId: string, request: ApplyPromotionRequest): Promise<MemberPromotion> {
        // The caller shows a richer success notification; suppress the generic one here to avoid a
        // double toast. Errors still surface via the base client.
        return this.post<MemberPromotion>(
            `/members/${membershipId}/apply-promotion`, request, { showSuccess: false });
    }

    // What applying would do, without doing it (FORT-318) — the numbers the confirmation panel shows.
    // Same body as applyPromotion, resolved by the same server-side pipeline, so the panel can't
    // disagree with what the subsequent apply commits.
    async previewPromotion(membershipId: string, request: ApplyPromotionRequest): Promise<PromotionPreview> {
        // No global spinner between the two modal steps — the Continue button carries its own
        // loading state. Errors still surface via the base client's notification.
        return this.post<PromotionPreview>(
            `/members/${membershipId}/apply-promotion/preview`, request,
            { showSuccess: false, showLoading: false });
    }

    // A member's applied-promotion history, newest first.
    async getMemberPromotions(membershipId: string): Promise<MemberPromotion[]> {
        const rows = await this.get<MemberPromotion[]>(
            `/members/${membershipId}/promotions`, undefined, { showLoading: false });
        return rows ?? [];
    }
}
